import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import ApiService from '../services/api';
import { t, pickLocalized } from '../i18n';
import { getHeaderPatternStyle, homeHeroBackgroundStyle, withSafeAreaTop, SAFE_AREA_BOTTOM } from './style';
// 主色淡底：以前是 `mainColor + '15'` 字符串拼接（把 hex 的 alpha 两位直接接在
// 六位色值后面）。主色一旦是 rgba(...) 或 8 位 hex，拼出来就是非法 CSS，整条
// background 声明被浏览器丢掉——图标托盘直接变透明。withAlpha 认所有颜色形态。
// 0.082 = 原来的十六进制 15 (=21/255)，像素不变。
import { withAlpha } from './theme-color';
import { conversationsSurface, CONV_LOADING } from './home-logic.js';
import { openLink as bridgeOpenLink } from './native-bridge.js';
import offlineStatusLine from './offline-status';
import { resolveHomeIdentity, resolveServiceTitle, conversationFirstBlocks, conversationRowTitle } from './launcher-engagement.js';

// 防御性二次检查：home_links.url 只放行 http(s)/mailto。后端 updateWidgetConfig
// 已经做过协议白名单校验（真正防线），这里只是不信任传下来的数据、渲染前再挡一次
// javascript: / data: 之类的注入向量。
const SAFE_URL_SCHEMES = ['http:', 'https:', 'mailto:'];
function isSafeExternalUrl(url) {
    if (typeof url !== 'string' || !url) return false;
    try {
        const parsed = new URL(url, typeof window !== 'undefined' ? window.location.href : undefined);
        return SAFE_URL_SCHEMES.includes(parsed.protocol);
    } catch (_) {
        return false;
    }
}

const MAX_HOME_LINKS = 5;

// Home 首页卡片编排（widget_config.home_blocks）—— 商户决定首页显示哪些区块、
// 以及它们的顺序（对标 Intercom messenger home 可配的卡片列表）。这里的默认顺序
// 必须和后端 ApplicationController::defaultHomeBlocks() 保持一致：发消息 → 搜索 →
// 知识库文章 → 自定义外链。注意 search 排在 articles 前面 —— 是这次改版特意调整的
// 默认值（搜索是主要入口，沉底不好找），不是历史行为的延续，商户可以在面板里调回去。
const DEFAULT_HOME_BLOCKS = [
    { type: 'conversations', enabled: true },
    { type: 'search', enabled: true },
    { type: 'articles', enabled: true },
    { type: 'links', enabled: true },
];
const KNOWN_HOME_BLOCK_TYPES = DEFAULT_HOME_BLOCKS.map((b) => b.type);


export default class HomeView extends Component {
    constructor(props) {
        super(props);
        this.state = {
            latestChat: null,
            recentChats: [],
            articles: [],
            searchResults: null,
            searchQuery: '',
            loading: true,
            hoveredAgent: null,
            tooltipPos: null,
        };
        this._showTooltip = (i, el) => {
            if (!el) return;
            const r = el.getBoundingClientRect();
            this.setState({
                hoveredAgent: i,
                tooltipPos: { top: r.bottom + 8, left: r.left + r.width / 2 },
            });
        };
        this._hideTooltip = () => this.setState({ hoveredAgent: null, tooltipPos: null });
        this._searchTimer = null;
    }

    componentDidMount() {
        this.loadData();
    }

    componentWillUnmount() {
        if (this._searchTimer) clearTimeout(this._searchTimer);
    }

    async loadData() {
        const { appId } = this.props;
        if (!appId) return;

        try {
            const [chatsRes, articlesRes] = await Promise.all([
                ApiService.getWidgetChats(appId, '').catch(() => null),
                this.isKnowledgeEnabled()
                    ? ApiService.getKnowledgeList(appId).catch(() => null)
                    : Promise.resolve(null),
            ]);

            const chats = chatsRes?.data || chatsRes?.chats || [];
            const latestChat = Array.isArray(chats) && chats.length > 0 ? chats[0] : null;
            // 最近 1-3 条会话卡片（Crisp/Intercom 结构）：home 必须给回到会话的路
            const recentChats = Array.isArray(chats) ? chats.slice(0, 3) : [];

            let articles = [];
            if (articlesRes) {
                const list = articlesRes?.data || articlesRes?.articles || [];
                articles = Array.isArray(list) ? list.slice(0, 5) : [];
            }

            this.setState({ latestChat, recentChats, articles, loading: false });
        } catch (e) {
            console.error('[HomeView] loadData error:', e);
            this.setState({ loading: false });
        }
    }

    isKnowledgeEnabled() {
        const { appConfig } = this.props;
        return !appConfig || (appConfig.knowledge_enabled !== false && appConfig.knowledge_enabled !== 0);
    }

    // widget_config.home_links —— Home 首页自定义外链卡片（对标 Intercom messenger
    // home 的 "Visit our website" 卡）。theme.homeLinks 由 widget.js 的 applyConfig()
    // 原样透传，这里再做一次防御性过滤 + 排序 + 数量上限，任何一步没通过就整条丢弃
    // （不渲染半条坏卡片，好过因为一条脏数据崩掉整个首页）。
    getHomeLinks() {
        const { theme } = this.props;
        const raw = Array.isArray(theme?.homeLinks) ? theme.homeLinks : [];
        return raw
            .filter((link) => link && typeof link === 'object' && isSafeExternalUrl(link.url))
            .map((link, index) => ({
                ...link,
                _sort: typeof link.sort === 'number' ? link.sort : index,
            }))
            .sort((a, b) => a._sort - b._sort)
            .slice(0, MAX_HOME_LINKS);
    }

    // widget_config.home_blocks —— 决定首页区块顺序 + 开关。后端 updateWidgetConfig
    // 已经把已知 type 白名单、去重、补齐做完了，这里再做一次防御性兜底：拿到的不是
    // 数组 / 为空 / 全是未知 type，一律回落默认顺序 —— 任何一条脏数据都不能让首页
    // 崩掉（比如旧版后端没有这个字段、或中间层缓存吐出了过期数据）。
    getHomeBlocks() {
        const { theme } = this.props;
        const raw = Array.isArray(theme?.homeBlocks) ? theme.homeBlocks : [];
        const filtered = raw.filter(
            (block) => block && typeof block === 'object' && KNOWN_HOME_BLOCK_TYPES.includes(block.type)
        );
        return filtered.length > 0 ? filtered : DEFAULT_HOME_BLOCKS;
    }

    handleSearch = (e) => {
        const query = e.target.value;
        this.setState({ searchQuery: query });

        if (this._searchTimer) clearTimeout(this._searchTimer);

        if (!query.trim()) {
            this.setState({ searchResults: null });
            return;
        }

        this._searchTimer = setTimeout(async () => {
            try {
                const res = await ApiService.searchKnowledge(query, this.props.appId);
                const results = res?.data || res?.articles || [];
                this.setState({ searchResults: Array.isArray(results) ? results : [] });
            } catch (e) {
                console.error('[HomeView] search error:', e);
            }
        }, 500);
    }

    navigate = (target, data) => {
        if (this.props.onNavigate) {
            this.props.onNavigate(target, data);
        }
    }

    // 以下四个 render* 方法是从原来写死顺序的 render() 里原样搬出来的 ——
    // 只是拆成独立方法方便按 home_blocks 的顺序调用，不改各区块内部的样式和交互。
    _formatChatTime = (isoString) => {
        if (!isoString) return '';
        try {
            const date = new Date(isoString);
            const diffDays = Math.floor((Date.now() - date.getTime()) / 86400000);
            if (diffDays <= 0) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            if (diffDays === 1) return t('chat.yesterday');
            if (diffDays < 7) return t('chat.days_ago', { days: diffDays });
            return date.toLocaleDateString();
        } catch (e) { return ''; }
    }

    /**
     * 会话列表加载态：两行骨架，几何照抄真实会话行，所以内容到位时只是"骨架
     * 变成字"，没有结构变化。
     *
     * 用骨架而不是 spinner/进度条：R4 定的加载语言就是骨架，且明确禁止一个平台
     * 存在两套加载态。复用 `.lc-skeleton-block`（中性灰 + 1400ms 呼吸 +
     * prefers-reduced-motion 兜底），不新造样式。
     *
     * 不加 R4 的 300ms 延迟：那条是给**首屏**骨架的（避免在空面板上闪一下整屏
     * 骨架）。这里 Home 已经画好了，延迟只会多出"空白→骨架→内容"第三档。
     *
     * 整块不可点击 —— 这才是真正的修复点。原先那张会被换掉的卡是 role=button，
     * 手指落下去点到的是即将消失的东西。
     */
    renderConversationsSkeleton() {
        const rowStyle = {
            display: 'flex', alignItems: 'center', gap: 'var(--lc-space-3)',
            padding: 'var(--lc-space-3) var(--lc-space-4)',
        };
        const bar = (w, h) => (
            <div className="lc-skeleton-block" style={{ width: w, height: h }} />
        );
        return (
            <div
                style={{ ...styles.card, padding: 0, overflow: 'hidden', cursor: 'default' }}
                aria-busy="true"
                aria-label={t('common.loading')}
            >
                {[0, 1].map((i) => (
                    <div
                        key={i}
                        style={{
                            ...rowStyle,
                            borderBottom: i === 0 ? 'var(--lc-hairline)' : 'none',
                        }}
                    >
                        {/* 与真实行的 cardIcon 同尺寸(40)，避免内容到位时左列位移 */}
                        <div
                            className="lc-skeleton-block"
                            style={{ width: 40, height: 40, flexShrink: 0 }}
                        />
                        <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 6 }}>
                            {/* 55% / 35% + 12 / 10 —— skeleton.geometry 契约的标题两行 */}
                            {bar('55%', 12)}
                            {bar('35%', 10)}
                        </div>
                        {/* 右侧时间戳占位 */}
                        <div
                            className="lc-skeleton-block"
                            style={{ width: 28, height: 10, flexShrink: 0 }}
                        />
                    </div>
                ))}
            </div>
        );
    }

    renderConversationsBlock() {
        const { theme } = this.props;
        const { latestChat, recentChats } = this.state;
        const mainColor = theme?.mainColor || '#2563eb';

        // 三态显式分档。此前这里只判 `recentChats.length > 0`，加载中恒为 false，
        // 于是回访访客先看到一张**可点击**的「发起新对话」卡，`getWidgetChats`
        // 回来后整块换成会话列表 + 「查看全部对话」，卡片高度一起跳。慢网/WebView
        // 下这个窗口足够长到手指已经按下去 —— 点到的是刚被换掉的那张卡。
        // Android/iOS 早就有加载态（注释里写着这正是真机报的「我的会话不见了」），
        // 只有 web 漏了。
        if (conversationsSurface(this.state) === CONV_LOADING) {
            return this.renderConversationsSkeleton();
        }

        // 有历史会话：最近 1-3 条会话卡片 + "查看全部对话 →"（回到会话列表的明确路）
        if (recentChats && recentChats.length > 0) {
            return this.renderRecentChats();
        }

        return (
            <div
                style={styles.card}
                role="button"
                tabIndex={0}
                onClick={() => this.navigate('chat', latestChat ? { chatId: latestChat.id } : undefined)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        this.navigate('chat', latestChat ? { chatId: latestChat.id } : undefined);
                    }
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--lc-shadow-2)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = styles.card.boxShadow; }}
            >
                <div style={styles.cardHeader}>
                    <div style={{ ...styles.cardIcon, background: withAlpha(mainColor, 0.082), color: mainColor }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        </svg>
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={styles.cardTitle}>{t('home.send_message')}</div>
                        <div style={styles.cardSubtitle}>
                            {latestChat?.last_message
                                ? t('status.recent_chat', { text: `${latestChat.last_message.substring(0, 30)}${latestChat.last_message.length > 30 ? '...' : ''}` })
                                : t('home.start_new')
                            }
                        </div>
                    </div>
                    {/* 颜色专项：描边原为写死 #94a3b8。它确实等于亮色 --lc-text-3，
                        但暗色下 text-3 是 #717a8c —— 写死就不跟。currentColor +
                        父级 color 让它跟着 token 走。 */}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="2" style={{ color: 'var(--lc-text-3, #94a3b8)' }}>
                        <path d="M9 18l6-6-6-6"/>
                    </svg>
                </div>
            </div>
        );
    }

    // Conversation-first layout: a primary card that stays put while recents load below it,
    // so the first thing a visitor can tap never changes under their finger.
    renderConversationFirstBlock() {
        const { theme } = this.props;
        const mainColor = theme?.mainColor || '#2563eb';
        const { recentChats } = this.state;
        const onlineCount = theme?.agentsOnlineCount || 0;
        const responseLine = offlineStatusLine(theme) || (onlineCount > 0 ? t('status.agents_online', { count: onlineCount }) : t('home.start_new'));
        const start = () => this.navigate('chat');
        const loading = conversationsSurface(this.state) === CONV_LOADING;

        return (
            <>
                <div
                    data-lc-home-start="1"
                    style={{ ...styles.card, padding: 'var(--lc-space-4)' }}
                    role="button"
                    tabIndex={0}
                    onClick={start}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); start(); } }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--lc-shadow-2)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = styles.card.boxShadow; }}
                >
                    <div style={styles.cardHeader}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ ...styles.cardTitle, fontSize: '16px' }}>{t('home.start_conversation')}</div>
                            <div style={{ ...styles.cardSubtitle, fontSize: '13px', color: 'var(--lc-text-2, #64748b)', marginTop: '4px' }}>{responseLine}</div>
                        </div>
                        <div style={{ ...styles.cardIcon, width: '40px', height: '40px', borderRadius: 'var(--lc-radius-full)', background: mainColor, color: '#fff' }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M5 12h14M12 5l7 7-7 7"/>
                            </svg>
                        </div>
                    </div>
                </div>
                {loading ? this.renderConversationsSkeleton() : (recentChats.length > 0 ? this.renderRecentChats() : null)}
            </>
        );
    }

    renderRecentChats() {
        const { theme, appConfig } = this.props;
        const { recentChats } = this.state;
        const mainColor = theme?.mainColor || '#2563eb';
        const serviceTitle = resolveServiceTitle(appConfig, t('brand.service_center'));
        return (
                <div style={{ ...styles.card, padding: 0, overflow: 'hidden', cursor: 'default' }}>
                    {recentChats.map((chat, i) => (
                        <div
                            key={chat.id || i}
                            role="button"
                            tabIndex={0}
                            onClick={() => this.navigate('chat', { chatId: chat.id })}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.navigate('chat', { chatId: chat.id }); } }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--lc-surface-2, #f8fafc)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 'var(--lc-space-3)',
                                // 左右各 16px 对称内边距；时间/头像与文字间 12px（flex gap）
                                padding: 'var(--lc-space-3) var(--lc-space-4)',
                                borderBottom: 'var(--lc-hairline)',
                                cursor: 'pointer',
                                transition: 'background var(--lc-dur-fast) var(--lc-ease-out)',
                            }}
                        >
                            <div style={{ ...styles.cardIcon, background: withAlpha(mainColor, 0.082), color: mainColor, flexShrink: 0 }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                </svg>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ ...styles.cardTitle, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {conversationRowTitle(chat.last_message, t('chat.conversation_fallback'))}
                                </div>
                                <div style={{ ...styles.cardSubtitle, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {serviceTitle}
                                </div>
                            </div>
                            <span style={{ fontSize: '11px', color: 'var(--lc-text-3, #94a3b8)', flexShrink: 0 }}>
                                {this._formatChatTime(chat.last_message_time)}
                            </span>
                        </div>
                    ))}
                    {/* 查看全部对话 → 会话列表视图 */}
                    <button
                        onClick={() => this.navigate('chat', { showList: true })}
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--lc-space-2)',
                            width: '100%', border: 'none', background: 'transparent',
                            padding: '11px 16px', cursor: 'pointer',
                            fontSize: '13px', fontWeight: 600, color: mainColor,
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--lc-surface-2, #f8fafc)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                        {t('home.view_all_chats')}
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 12h14M12 5l7 7-7 7"/>
                        </svg>
                    </button>
                </div>
        );
    }

    // articles 与 search 原来是同一个 knowledgeEnabled 分支里的兄弟节点，现在拆成两个
    // 独立区块（可以被 home_blocks 排到不相邻的位置），所以各自都要重复判断
    // knowledgeEnabled —— 知识库关掉时这两块都不渲染，即使 home_blocks 里 enabled=true。
    renderArticlesBlock() {
        if (!this.isKnowledgeEnabled()) return null;

        const { theme } = this.props;
        const { articles, searchResults, loading } = this.state;
        const mainColor = theme?.mainColor || '#2563eb';
        const displayArticles = searchResults !== null ? searchResults : articles;

        // 没有文章可展示时整块跳过 —— 否则只剩一个"帮助文章"标题孤零零挂在那儿，
        // 下面什么都没有（知识库为空的 app 全都是这个样子）。
        // 两种情况仍要渲染：还在加载（给 loading 文案）、以及搜索无结果
        // （要告诉用户"没搜到"，而不是整块消失让人以为搜索坏了）。
        if (!loading && searchResults === null && displayArticles.length === 0) {
            return null;
        }

        return (
            <>
                <div style={styles.sectionTitle}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={mainColor} strokeWidth="2">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                    </svg>
                    {searchResults !== null ? t('home.search_results') : t('home.help_articles')}
                </div>

                {loading ? (
                    <div style={styles.loadingText}>{t('common.loading')}</div>
                ) : displayArticles.length > 0 ? (
                    <div style={styles.articleList}>
                        {displayArticles.map((article, i) => (
                            <div
                                key={article.id || i}
                                style={styles.articleItem}
                                role="button"
                                tabIndex={0}
                                onClick={() => this.navigate('knowledge', { articleId: article.id, article })}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        this.navigate('knowledge', { articleId: article.id, article });
                                    }
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--lc-surface-2, #f8fafc)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" style={{ flexShrink: 0, marginTop: '2px' }}>
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                    <polyline points="14 2 14 8 20 8"/>
                                    <line x1="16" y1="13" x2="8" y2="13"/>
                                    <line x1="16" y1="17" x2="8" y2="17"/>
                                </svg>
                                <span style={styles.articleTitle}>{article.title || article.name}</span>
                            </div>
                        ))}
                    </div>
                ) : searchResults !== null ? (
                    <div style={styles.loadingText}>{t('home.no_articles')}</div>
                ) : null}
            </>
        );
    }

    renderSearchBlock() {
        if (!this.isKnowledgeEnabled()) return null;

        const { searchQuery } = this.state;

        return (
            <div style={styles.searchWrapper}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" style={{ flexShrink: 0 }}>
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                    type="text"
                    value={searchQuery}
                    onChange={this.handleSearch}
                    placeholder={t('home.search_articles')}
                    style={styles.searchInput}
                />
            </div>
        );
    }

    // Custom home link cards（对标 Intercom messenger home 的 "Visit our website" 卡）——
    // 商户在面板配置的外链。空数组什么都不渲染，不留标题/占位容器。
    renderLinksBlock() {
        const { theme } = this.props;
        const mainColor = theme?.mainColor || '#2563eb';
        const homeLinks = this.getHomeLinks();
        if (homeLinks.length === 0) return null;

        return (
            <>
                {homeLinks.map((link) => {
                    const title = pickLocalized(link.title) || link.url;
                    const openLink = () => bridgeOpenLink(link.url, { source: 'home_link' });
                    return (
                        <div
                            key={link.id || link.url}
                            style={styles.card}
                            role="button"
                            tabIndex={0}
                            onClick={openLink}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    openLink();
                                }
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--lc-shadow-2)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = styles.card.boxShadow; }}
                        >
                            <div style={styles.cardHeader}>
                                <div style={{ ...styles.cardIcon, background: withAlpha(mainColor, 0.082), color: mainColor }}>
                                    {link.icon
                                        ? <span style={{ fontSize: '18px', lineHeight: 1 }}>{link.icon}</span>
                                        : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                                            <polyline points="15 3 21 3 21 9"/>
                                            <line x1="10" y1="14" x2="21" y2="3"/>
                                          </svg>
                                    }
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={styles.cardTitle}>{title}</div>
                                </div>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                                    <polyline points="15 3 21 3 21 9"/>
                                    <line x1="10" y1="14" x2="21" y2="3"/>
                                </svg>
                            </div>
                        </div>
                    );
                })}
            </>
        );
    }

    render() {
        const { theme, appConfig } = this.props;
        // header 高度：欢迎区据此把渐变/纹样接着 header 继续画（默认 72 = 默认头部高度）。
        // 必须读 prop —— header 是几行、多高由 widget.js 那边决定，这里写死一个数字，
        // header 一改档接缝立刻错位。
        //
        // 沉浸式状态栏适配：fill 布局下 header 的渲染高度自己额外加了一份
        // env(safe-area-inset-top)（见 widget.js 的 renderLargeHeader 等），但
        // props.headerHeight 传的是不含安全区的原始数字（widget.js 的
        // getHeaderHeight() 出于「内容区靠 flex:1 自适应，不需要精确数字」的
        // 理由没有跟着改）——这里补上同一份 env()，否则欢迎区的渐变/纹样接续
        // 偏移会跟 header 的真实渲染高度差一截，接缝处出现色差。isFillFullPage
        // 判断口径跟 widget.js 的 _isFillFullPage() 完全一致（mode+layout 都
        // 在 theme 上，这里没有单独的方法可调，直接复用同一对字段）。
        const isFillFullPage = theme && theme.mode === 'inline' && theme.layout === 'fill';
        const heroOffsetBase = Number(this.props.headerHeight) || 72;
        const heroOffset = withSafeAreaTop(heroOffsetBase, isFillFullPage);
        const mainColor = theme?.mainColor || '#2563eb';
        const identity = resolveHomeIdentity(theme?.headerAgents, theme?.logoUrl);
        const agents = identity.kind === 'agents' ? identity.agents : [];
        const onlineCount = theme?.agentsOnlineCount || 0;
        const serviceTitle = resolveServiceTitle(appConfig, t('brand.service_center'));
        // 状态文案可能整个不存在（没配 avgReplyTime、也没人在线且 offline 文案为空）——
        // 那就连状态行都不渲染，不留一个只有圆点的空行
        const statusText = onlineCount > 0
            ? t('status.agents_online', { count: onlineCount })
            : (offlineStatusLine(theme) || t('status.offline'));
        const statusColor = onlineCount > 0 ? '#4ade80' : '#94a3b8';

        // Home 首页卡片编排：按 home_blocks 的顺序依次渲染，enabled:false 的跳过。
        // 各区块内部仍然保留自己的显示条件（knowledgeEnabled / homeLinks 是否为空
        // 等），home_blocks 只决定"要不要放这块 + 放在哪个位置"。
        const conversationFirst = theme?.homeLayout === 'conversation_first';
        const homeBlocks = conversationFirst ? conversationFirstBlocks(this.getHomeBlocks()) : this.getHomeBlocks();
        const blockRenderers = {
            conversations: () => (conversationFirst ? this.renderConversationFirstBlock() : this.renderConversationsBlock()),
            search: () => this.renderSearchBlock(),
            articles: () => this.renderArticlesBlock(),
            links: () => this.renderLinksBlock(),
        };

        return (
            <div style={styles.container}>
                {/* Welcome Area —— 与上方 header 拼成一条连续的 hero。
                    header 和这里画的是**同一个虚拟渐变盒**（见 style.js 的
                    HOME_HERO_BG_HEIGHT），这里按 header 高度向上偏移，渐变才不会在
                    接缝处重新开始、跳一次色；纹样同理按同一偏移继续平铺，否则星星会
                    从这块的左上角重新起铺，看起来就是"星星没下来、下面是单独一块"。

                    偏移**不再减去 scrollTop**：欢迎区已经从滚动容器里搬出来了，是
                    hero 之下内容区自己在滚，这块本身一动不动 —— 接缝恒定，减了反而
                    会让纹样跟着滚动漂走、把对齐弄坏。 */}
                <div style={{ ...styles.welcomeArea, ...homeHeroBackgroundStyle(theme, heroOffset) }}>
                    <div style={getHeaderPatternStyle(theme?.headerPattern, heroOffset)} />

                    {identity.kind === 'logo' && (
                        <div style={styles.avatarRow}>
                            <div style={styles.avatar}>
                                <img src={identity.url} style={styles.avatarImg} alt="" />
                            </div>
                        </div>
                    )}

                    {/* Agent avatars - hover/touch 显示客服名 */}
                    {agents.length > 0 && (
                        <div style={styles.avatarRow}>
                            {agents.slice(0, 3).map((agent, i) => (
                                <div
                                    key={i}
                                    style={{
                                        ...styles.avatar,
                                        marginLeft: i === 0 ? 0 : '-8px',
                                        zIndex: this.state.hoveredAgent === i ? 100 : (3 - i),
                                        transform: this.state.hoveredAgent === i ? 'translateY(-2px) scale(1.1)' : 'none',
                                        transition: 'transform var(--lc-dur-base) var(--lc-ease-out), z-index 0s',
                                        position: 'relative',
                                        cursor: 'pointer',
                                    }}
                                    title={agent.name || t('brand.default_title')}
                                    onMouseEnter={(e) => this._showTooltip(i, e.currentTarget)}
                                    onMouseLeave={() => this._hideTooltip()}
                                    onTouchStart={(e) => this._showTooltip(i, e.currentTarget)}
                                    onTouchEnd={() => setTimeout(() => this._hideTooltip(), 1500)}
                                >
                                    {agent.avatar_url
                                        ? <img src={agent.avatar_url} style={styles.avatarImg} alt={agent.name || ''} />
                                        : <span style={{ color: mainColor, fontWeight: 600, fontSize: '14px' }}>
                                            {agent.name?.charAt(0)?.toUpperCase() || 'A'}
                                          </span>
                                    }
                                    {/* 在线状态绿点（右下角） */}
                                    {agent.online !== false && (
                                        <span style={{
                                            position: 'absolute',
                                            right: 0,
                                            bottom: 0,
                                            width: '10px',
                                            height: '10px',
                                            borderRadius: 'var(--lc-radius-full)',
                                            background: '#22c55e',
                                            border: '2px solid #ffffff',
                                            boxSizing: 'border-box',
                                            pointerEvents: 'none',
                                        }} />
                                    )}
                                </div>
                            ))}
                            {/* Portal 到 document.body：彻底脱离 transform 父级的 stacking context */}
                            {this.state.hoveredAgent !== null
                                && this.state.tooltipPos
                                && agents[this.state.hoveredAgent]?.name
                                && typeof document !== 'undefined'
                                && ReactDOM.createPortal(
                                    <div style={{
                                        position: 'fixed',
                                        top: this.state.tooltipPos.top,
                                        left: this.state.tooltipPos.left,
                                        transform: 'translateX(-50%)',
                                        background: 'rgba(15, 23, 42, 0.92)',
                                        color: '#fff',
                                        fontSize: '11px',
                                        padding: '4px 10px',
                                        borderRadius: 'var(--lc-radius-sm)',
                                        whiteSpace: 'nowrap',
                                        pointerEvents: 'none',
                                        boxShadow: 'var(--lc-shadow-2)',
                                        zIndex: 2147483647,
                                        fontWeight: 500,
                                    }}>
                                        {agents[this.state.hoveredAgent].name}
                                    </div>,
                                    document.body
                                )
                            }
                        </div>
                    )}
                    <div style={styles.welcomeTitle}>{serviceTitle}</div>
                    {/* The conversation-first start card carries this line, so it is not repeated. */}
                    {statusText && !conversationFirst ? (
                        <div style={styles.welcomeStatus}>
                            <span style={{ ...styles.statusDot, background: statusColor }} />
                            {statusText}
                        </div>
                    ) : null}
                </div>

                {/* ============ 内容区：独立滚动 ============ */}
                <div data-lc-home-scroll="1" style={styles.content}>
                    {/* 卡片列表必须包一层：滚动容器自己**不能**是 flex 容器 ——
                        flex item 默认 flex-shrink:1，剩余高度不够时卡片会被压扁而不是
                        溢出，scrollHeight 永远等于 clientHeight，内容区根本滚不动
                        （实测：滚动搬到这一层的第一版就是这样，maxScroll 恒为 0）。
                        padding 也放在这层：滚动容器的 padding-bottom 在部分浏览器里
                        不计入 scrollHeight，放内层才稳。 */}
                    <div style={styles.contentInner}>
                        {homeBlocks.map((block) => {
                            if (!block || block.enabled === false) return null;
                            const renderBlock = blockRenderers[block.type];
                            if (!renderBlock) return null; // 未知 type：getHomeBlocks() 已经过滤过，这里是双重保险
                            const content = renderBlock();
                            if (!content) return null; // 区块自身条件不满足（如知识库关闭 / 无外链）
                            return <React.Fragment key={block.type}>{content}</React.Fragment>;
                        })}
                    </div>
                </div>
            </div>
        );
    }
}

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        // 父级 .home-widget 现在是 flex 列里的 `flex:1 1 auto; min-height:0`（面板
        // 打开态是 header + 内容区两段）。height:100% 在父高度确定时够用，但父级要是
        // 哪天变成不确定高度，百分比会退化成 auto、内容区就再也滚不动了 —— 这里同时
        // 声明 flex 尺寸 + min-height:0，两条路哪条成立都填满剩余高度。
        flex: '1 1 auto',
        minHeight: 0,
        background: 'var(--lc-surface, #fff)',
        // 滚动搬到内容区去了：外层再滚就等于 hero 也会被滚走
        overflow: 'hidden',
    },
    welcomeArea: {
        // 不滚动、不被 flex 拉伸或压缩：高度完全由内容决定（静态）
        flex: '0 0 auto',
        // 上内边距原来是 24px，收到 12px；下 20px → 14px。
        padding: '12px 20px 14px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        // 标题与状态行之间 6px（原来也是 6px，未动）
        gap: 'var(--lc-space-2)',
        // 背景纹样层是 absolute + inset:0，父级必须 relative 才不会跑到面板左上角；
        // 内容整体抬一层，压在纹样之上（纹样 zIndex:0）。
        position: 'relative',
        isolation: 'isolate',
    },
    avatarRow: {
        display: 'flex',
        alignItems: 'center',
        // 头像组与标题之间 10px（= marginBottom 4 + 父级 gap 6，与改版前一致）
        marginBottom: 'var(--lc-space-1)',
        // 抬到背景纹样之上。welcomeArea 是 flex 容器，flex item 上的 z-index
        // 无需 position 即生效（与聊天页 header 里内容的写法一致）。
        zIndex: 1,
    },
    avatar: {
        width: '36px',
        height: '36px',
        borderRadius: 'var(--lc-radius-full)',
        border: '2px solid rgba(255,255,255,0.8)',
        background: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'relative',
    },
    avatarImg: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    welcomeTitle: {
        color: '#fff',
        fontSize: '18px',
        fontWeight: 600,
        textAlign: 'center',
        zIndex: 1,   // 压在背景纹样之上（同 avatarRow）
    },
    welcomeStatus: {
        color: 'rgba(255,255,255,0.85)',
        fontSize: '13px',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--lc-space-2)',
        zIndex: 1,   // 压在背景纹样之上（同 avatarRow）
    },
    statusDot: {
        display: 'inline-block',
        width: '7px',
        height: '7px',
        borderRadius: 'var(--lc-radius-full)',
    },
    content: {
        // 独立滚动区：hero 之下的全部剩余高度。minHeight:0 是 flex 里 overflow
        // 能生效的前提（默认 min-height:auto 会让它被内容顶高、永远不出滚动条）。
        flex: '1 1 auto',
        minHeight: 0,
        overflowY: 'auto',
        // 滚到头不要把宿主页面一起带着滚（widget 是浮层）
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        // 卡片区给浅灰底、卡片保持纯白：此前页面底和卡片同为 #fff，只靠
        // 1px #f1f5f9 描边区分 —— 那条描边压在白底上肉眼几乎看不见，首页看起来
        // 是一片白底上散落的文字块，读不出卡片层次。改成用底色分层立卡
        // （成熟 messenger 的通行做法），而不是把描边加深到刺眼。
        //
        // 选色依据：相邻浅灰之间对比度天然逼近 1，WCAG 3.0 那个门槛是给
        // "承载信息的边界"的，不适用于这种同色系分层；真正决定能不能看出卡片的是
        // 相对亮度差 + 阴影。#f1f5f9 与白卡亮度差 0.09（#f8fafc 只有 0.046，
        // 第一版选它偏弱），既能分层又不发脏。
        background: 'var(--lc-bg, #f1f5f9)',
    },
    contentInner: {
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--lc-space-3)',
        padding: 'var(--lc-space-4)',
        // iPhone 无 Home 键机型：最后一张卡片/链接不能贴着手势条。跟 header 顶部
        // 那份不同，这里不用按 fill 布局门控——env() 在非沉浸场景天然是 0，多留
        // 一点底部呼吸空间即使不需要也无害，不像顶部内容那样"多了会跟居中逻辑
        // 打架"，没必要为了严格对齐 _isFillFullPage() 而把这个静态样式表拆成
        // 运行时计算。
        paddingBottom: `calc(16px + ${SAFE_AREA_BOTTOM})`,
    },
    card: {
        // 纯白卡压在 content 的浅灰底上（见 content 注释），靠底色反差立卡。
        background: 'var(--lc-surface, #fff)',
        border: 'var(--lc-hairline)',
        borderRadius: 'var(--lc-radius-md)',
        padding: '14px 16px',
        cursor: 'pointer',
        // 静置就带一层极轻的阴影，hover 时加重（hover 的 boxShadow 在渲染处内联设置）。
        boxShadow: 'var(--lc-shadow-1)',
        transition: 'box-shadow var(--lc-dur-base) var(--lc-ease-out)',
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--lc-space-3)',
    },
    cardIcon: {
        width: '40px',
        height: '40px',
        borderRadius: 'var(--lc-radius-sm)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    cardTitle: {
        fontSize: '15px',
        fontWeight: 600,
        color: 'var(--lc-text, #1e293b)',
        lineHeight: '1.3',
    },
    cardSubtitle: {
        fontSize: '12px',
        color: 'var(--lc-text-3, #94a3b8)',
        lineHeight: '1.4',
        marginTop: '2px',
    },
    sectionTitle: {
        fontSize: '14px',
        fontWeight: 600,
        color: 'var(--lc-text, #334155)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--lc-space-2)',
        marginTop: 'var(--lc-space-1)',
    },
    articleList: {
        // 与 card 同一套外观：纯白压在 content 的浅灰底上。
        background: 'var(--lc-surface, #fff)',
        border: 'var(--lc-hairline)',
        borderRadius: 'var(--lc-radius-md)',
        overflow: 'hidden',
        boxShadow: 'var(--lc-shadow-1)',
    },
    articleItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--lc-space-2)',
        padding: '10px 14px',
        cursor: 'pointer',
        transition: 'background var(--lc-dur-fast) var(--lc-ease-out)',
        // 分隔线原本是 #f8fafc，压在白卡上对比度约 1.05，等于看不见。
        // 与卡片描边同色，弱但可见。
        borderBottom: 'var(--lc-hairline)',
    },
    articleTitle: {
        fontSize: '13px',
        color: 'var(--lc-text-2, #475569)',
        lineHeight: '1.4',
    },
    searchWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--lc-space-2)',
        // 原来是 #f8fafc 灰底 —— content 改成同色浅灰底之后它会整个融进背景、
        // 看不出是个输入框，所以改成白底 + 同款描边，和卡片保持一套外观。
        background: 'var(--lc-surface, #fff)',
        border: 'var(--lc-hairline)',
        borderRadius: 'var(--lc-radius-sm)',
        padding: '10px 14px',
        boxShadow: 'var(--lc-shadow-1)',
    },
    searchInput: {
        border: 'none',
        background: 'transparent',
        outline: 'none',
        fontSize: '13px',
        color: 'var(--lc-text, #334155)',
        flex: 1,
        lineHeight: '1.4',
    },
    loadingText: {
        fontSize: '13px',
        color: 'var(--lc-text-3, #94a3b8)',
        textAlign: 'center',
        padding: 'var(--lc-space-3)',
    },
};
